/* ============================================================================
 * TextField.jsx
 * ============================================================================
 * Componente de campo de texto editable con label flotante animado.
 *
 * Campo de entrada de texto estándar con label que se desplaza hacia arriba
 * cuando el campo está enfocado, tiene contenido o placeholder. Incluye
 * manejo de validación con mensajes de error animados, truncamiento de texto
 * largo con ellipsis, y soporte completo para accesibilidad.
 *
 * Características:
 * - Label flotante animado
 * - Validación visual con mensajes de error
 * - Truncamiento de texto largo con ellipsis
 * - Estados: normal, disabled, hidden, invalid
 * - Soporte para autoComplete
 * - Forward ref para acceso directo al input
 * - Tipos de input configurables (text, email, tel, url, search)
 *
 * Accesibilidad:
 * - aria-invalid para campos con error
 * - aria-describedby para vincular mensajes de error
 * - role="alert" en mensajes de error
 * - Asociación label-input mediante htmlFor/id
 *
 * @module components/TextField
 * ========================================================================== */

import { useMemo, useState, forwardRef, useCallback, useRef } from "react";

import classNames from "classnames";

import { isEmpty } from "../../utils/validators";

/* ============================================================================
 * Constantes
 * ========================================================================== */

/**
 * Tipos de input permitidos.
 * Limitados a tipos de texto para mantener la coherencia del componente.
 * @type {Set<string>}
 */
const ALLOWED_INPUT_TYPES = new Set(["text", "email", "tel", "url", "search"]);

/**
 * Tipo de input por defecto.
 * @type {string}
 */
const DEFAULT_INPUT_TYPE = "text";

/* ============================================================================
 * Tipos y documentación de Props
 * ========================================================================== */

/**
 * @typedef {Object} TextFieldProps
 * @property {string} name - Identificador único del campo (requerido para accesibilidad)
 * @property {string} [label] - Etiqueta flotante del campo
 * @property {string} [placeholder] - Texto placeholder
 * @property {string} [type="text"] - Tipo de input (text, email, tel, url, search)
 * @property {string} [autoComplete] - Atributo autocomplete del input
 * @property {boolean} [disabled=false] - Campo deshabilitado
 * @property {boolean} [hidden=false] - Campo oculto
 * @property {boolean} [required=false] - Campo requerido
 * @property {string} [value] - Valor del campo
 * @property {string} [error] - Mensaje de error
 * @property {boolean} [showValueInTitle=true] - Mostrar valor en el atributo title
 * @property {Function} [onChange] - Callback al cambiar el valor
 * @property {Function} [onFocus] - Callback al enfocar el campo
 * @property {Function} [onBlur] - Callback al perder el foco
 * @property {Function} [onCopy] - Callback al copiar contenido
 * @property {Function} [onPaste] - Callback al pegar contenido
 */

/* ============================================================================
 * Componente
 * ========================================================================== */

/**
 * Campo de texto editable con label flotante.
 *
 * @component
 * @param {TextFieldProps} props - Propiedades del componente
 * @param {React.Ref} ref - Referencia al elemento input
 * @returns {JSX.Element} Campo de texto
 *
 * @example
 * // Campo básico
 * <TextField
 *   name="name"
 *   label="Nombre"
 *   value={name}
 *   onChange={handleChange}
 * />
 *
 * @example
 * // Campo de email con validación
 * <TextField
 *   name="email"
 *   type="email"
 *   label="Correo electrónico"
 *   value={email}
 *   error={emailError}
 *   required
 *   onChange={handleChange}
 * />
 *
 * @example
 * // Campo sensible sin valor en title
 * <TextField
 *   name="apiKey"
 *   label="API Key"
 *   value={apiKey}
 *   showValueInTitle={false}
 *   onChange={handleChange}
 * />
 */
const TextField = forwardRef((props, ref) => {
  const {
    name,
    label,
    placeholder,
    type = DEFAULT_INPUT_TYPE,
    autoComplete,
    disabled = false,
    hidden = false,
    required = false,
    value = "",
    error,
    showValueInTitle = true,
    onChange,
    onFocus,
    onBlur,
    onCopy,
    onPaste,
    ...rest
  } = props;

  /* --------------------------------------------------------------------------
   * Refs
   * ------------------------------------------------------------------------ */

  /**
   * Ref interna para acceder al input cuando la ref externa es una función.
   */
  const inputRef = useRef(null);

  /* --------------------------------------------------------------------------
   * Estado local
   * ------------------------------------------------------------------------ */

  /**
   * Estado de focus del input.
   * Se usa junto con value y placeholder para determinar si el label está activo.
   */
  const [isFocused, setIsFocused] = useState(false);

  /* --------------------------------------------------------------------------
   * Valores derivados
   * ------------------------------------------------------------------------ */

  /**
   * Tipo de input validado.
   * Si el tipo proporcionado no está permitido, usa el tipo por defecto.
   */
  const inputType = useMemo(() => {
    return ALLOWED_INPUT_TYPES.has(type) ? type : DEFAULT_INPUT_TYPE;
  }, [type]);

  /**
   * Determina si el label debe estar en posición "activa" (elevada).
   * Activo cuando: tiene focus, tiene valor, o tiene placeholder.
   *
   * Nota: Se deriva directamente de props y estado, no necesita sincronización
   * manual ya que React re-renderiza cuando cambian.
   */
  const isLabelActive = useMemo(() => {
    return isFocused || !isEmpty(value) || !isEmpty(placeholder);
  }, [isFocused, value, placeholder]);

  /**
   * Label formateado con asterisco si es requerido.
   */
  const formattedLabel = useMemo(() => {
    if (isEmpty(label)) return "";
    return `${label}${required ? " *" : ""}`;
  }, [label, required]);

  /**
   * Genera el título del componente para el atributo title.
   * Por seguridad, el valor solo se incluye si showValueInTitle es true.
   */
  const title = useMemo(() => {
    if (!showValueInTitle) {
      return label || "";
    }
    const content = value || placeholder;
    return [label, content].filter((i) => !isEmpty(i)).join(" - ");
  }, [value, placeholder, label, showValueInTitle]);

  /**
   * Indica si el campo tiene error.
   */
  const hasError = !isEmpty(error);

  /* --------------------------------------------------------------------------
   * Ref callback para combinar refs
   * ------------------------------------------------------------------------ */

  /**
   * Combina la ref interna con la ref forwarded.
   * Soporta tanto refs de objeto como refs de callback.
   *
   * @param {HTMLInputElement | null} el - Elemento input
   */
  const setRefs = useCallback(
    (el) => {
      inputRef.current = el;
      if (typeof ref === "function") {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    },
    [ref],
  );

  /* --------------------------------------------------------------------------
   * Handlers de eventos
   * ------------------------------------------------------------------------ */

  /**
   * Maneja el evento de foco.
   * Actualiza el estado de focus para el label animado.
   *
   * @param {React.FocusEvent} evt - Evento de focus
   */
  const handleFocus = useCallback(
    (evt) => {
      setIsFocused(true);
      onFocus?.(evt);
    },
    [onFocus],
  );

  /**
   * Maneja el evento de pérdida de foco.
   * Actualiza el estado de focus para el label animado.
   *
   * @param {React.FocusEvent} evt - Evento de blur
   */
  const handleBlur = useCallback(
    (evt) => {
      setIsFocused(false);
      onBlur?.(evt);
    },
    [onBlur],
  );

  /**
   * Maneja el evento de cambio de valor.
   * Simplemente propaga el evento al padre.
   *
   * @param {React.ChangeEvent} evt - Evento de cambio
   */
  const handleChange = useCallback(
    (evt) => {
      onChange?.(evt);
    },
    [onChange],
  );

  /**
   * Maneja el evento de copiar.
   *
   * @param {React.ClipboardEvent} evt - Evento de copiar
   */
  const handleCopy = useCallback(
    (evt) => {
      onCopy?.(evt);
    },
    [onCopy],
  );

  /**
   * Maneja el evento de pegar.
   *
   * @param {React.ClipboardEvent} evt - Evento de pegar
   */
  const handlePaste = useCallback(
    (evt) => {
      onPaste?.(evt);
    },
    [onPaste],
  );

  /* --------------------------------------------------------------------------
   * Render
   * ------------------------------------------------------------------------ */

  return (
    <div
      className={classNames("text-field__container", {
        "text-field__container--unlabelled": isEmpty(label),
        "text-field__container--hidden": hidden,
        "text-field__container--disabled": disabled,
        "text-field__container--invalid": hasError,
      })}
      title={title}
    >
      <div className="text-field__content">
        {/* Label flotante */}
        {formattedLabel && (
          <label
            htmlFor={name}
            className={classNames("text-field__label", "smaller", {
              "text-field__label--active": isLabelActive,
            })}
          >
            {formattedLabel}
          </label>
        )}

        {/* Input con truncamiento de texto */}
        <div className="text-field__ellipsis">
          <input
            ref={setRefs}
            id={name}
            name={name}
            type={inputType}
            className="text-field__input"
            placeholder={placeholder}
            value={value}
            disabled={disabled}
            hidden={hidden}
            required={required}
            autoComplete={autoComplete}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${name}-error` : undefined}
            onFocus={handleFocus}
            onChange={handleChange}
            onBlur={handleBlur}
            onCopy={handleCopy}
            onPaste={handlePaste}
            {...rest}
          />
        </div>
      </div>

      {/* Mensaje de error
       *
       * `role="alert"` solo cuando hay error (implícito `aria-live="assertive"`).
       * Sin `aria-live="polite"` adicional para evitar conflicto entre SRs
       * (combinar ambos producía comportamiento ambiguo). La asociación con
       * el input se mantiene via `aria-describedby={name}-error`.
       */}
      <p
        id={`${name}-error`}
        className={classNames("text-field__error", {
          "text-field__error--visible": hasError,
        })}
        title={error}
        role={hasError ? "alert" : undefined}
      >
        {error}
      </p>
    </div>
  );
});

/* --------------------------------------------------------------------------
 * Display Name para DevTools
 * ------------------------------------------------------------------------ */

TextField.displayName = "TextField";

export default TextField;
